import {isPassportIdentity} from '@sollapay/domain';
import {
  ASSET_FIELD_KEYS,
  getAssetFieldKeys,
  getAssetTypeDescriptor
} from '@sollapay/domain/asset-types';
import {
  AssetAttachmentType,
  AssetType,
  IdentityDocumentType,
  PartyType,
  PepPublicRole,
  PepRelationship,
  TaxResidency
} from '@sollapay/enums';
import {z} from 'zod';

import {YEAR_MONTH_RE} from '@/components/pep';
import {
  addEmailIssue,
  addFullNameIssue,
  addFutureDateIssue,
  addIndividualIdentityIssues,
  addMobilePhoneIssue
} from '@/lib/validation';

import type {AssetFieldKey} from '@sollapay/domain/asset-types';

const buyerPepPartySchema = z.object({
  relationship: z.union([z.nativeEnum(PepRelationship), z.literal('')]),
  fullName: z.string(),
  countryOfOffice: z.string(),
  publicRole: z.union([z.nativeEnum(PepPublicRole), z.literal('')]),
  stillInRole: z.boolean(),
  roleStartMonth: z.string(),
  roleEndMonth: z.string(),
  sourceOfWealth: z.string()
});

const buyerPepSchema = z.object({
  isPep: z.boolean().default(false),
  parties: z.array(buyerPepPartySchema).default([])
});

export type BuyerPepFormValues = z.infer<typeof buyerPepSchema>;

type PepParty = z.infer<typeof buyerPepPartySchema>;
type ZodCtx = z.RefinementCtx;

const addPepPartyIssues = (ctx: ZodCtx, parties: PepParty[], isCorporate: boolean) => {
  parties.forEach((party, i) => {
    if (isCorporate && !party.relationship) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pep', 'parties', i, 'relationship'],
        message: 'common:required'
      });
    }
    if (isCorporate && !party.fullName.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pep', 'parties', i, 'fullName'],
        message: 'common:required'
      });
    }
    if (!party.countryOfOffice.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pep', 'parties', i, 'countryOfOffice'],
        message: 'common:required'
      });
    }
    if (!party.publicRole) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pep', 'parties', i, 'publicRole'],
        message: 'common:required'
      });
    }
    if (!party.sourceOfWealth.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pep', 'parties', i, 'sourceOfWealth'],
        message: 'common:required'
      });
    }
    if (!YEAR_MONTH_RE.test(party.roleStartMonth)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pep', 'parties', i, 'roleStartMonth'],
        message: 'common:required'
      });
    }
    if (!party.stillInRole && !YEAR_MONTH_RE.test(party.roleEndMonth)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pep', 'parties', i, 'roleEndMonth'],
        message: 'common:required'
      });
    }
    if (
      !party.stillInRole &&
      YEAR_MONTH_RE.test(party.roleStartMonth) &&
      YEAR_MONTH_RE.test(party.roleEndMonth) &&
      party.roleEndMonth < party.roleStartMonth
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pep', 'parties', i, 'roleEndMonth'],
        message: 'activation.packages.trustDetails.form.errors.roleEndMonthBeforeStart'
      });
    }
  });
};

const AssetAttachmentSchema = z.object({
  type: z.nativeEnum(AssetAttachmentType),
  description: z.string().optional()
});

/** `''` because a general-sale form opens with nothing chosen — the combobox pins it after that. */
const AssetSchema = z
  .object({
    assetType: z.union([z.nativeEnum(AssetType), z.literal('')]),
    buildingNumber: z.string().default(''),
    unitNumber: z.string().default(''),
    address: z.string().default(''),
    floor: z.string().default(''),
    block: z.string().default(''),
    parcel: z.string().default(''),
    subParcel: z.string().default(''),
    area: z.string().default(''),
    description: z.string().default(''),
    attachments: z.array(AssetAttachmentSchema).default([])
  })
  .superRefine((data, ctx) => {
    if (!data.assetType) return;
    for (const field of getAssetTypeDescriptor(data.assetType).fields) {
      if (field.required && !data[field.key].trim()) {
        ctx.addIssue({code: z.ZodIssueCode.custom, path: [field.key], message: 'common:required'});
      }
    }
  });

export const BuyerEntrySchema = z
  .object({
    buyerKey: z.string(),
    fullName: z.string().min(1, 'common:required'),
    nationalId: z.string().min(1, 'common:required'),
    idType: z.nativeEnum(IdentityDocumentType),
    idCountry: z.string().default(''),
    identityDocumentId: z.string().default(''),
    dateOfBirth: z.string().min(1, 'common:required'),
    phone: z.string().min(1, 'common:required'),
    email: z.string().min(1, 'common:required'),
    address: z.string().min(1, 'common:required'),
    taxResidency: z
      .union([z.nativeEnum(TaxResidency), z.literal('')])
      .refine(v => v !== '', {message: 'common:required'}),
    taxResidencyCountry: z.string().default(''),
    pep: buyerPepSchema
  })
  .superRefine((data, ctx) => {
    if (data.taxResidency === TaxResidency.OTHER && !data.taxResidencyCountry.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['taxResidencyCountry'],
        message: 'common:required'
      });
    }

    addFutureDateIssue(ctx, 'dateOfBirth', data.dateOfBirth);
    addFullNameIssue(ctx, data.fullName);
    addMobilePhoneIssue(ctx, data.phone);
    addEmailIssue(ctx, data.email);

    addIndividualIdentityIssues(ctx, {
      isPassport: isPassportIdentity(data.idType),
      idNumber: data.nationalId,
      idCountry: data.idCountry,
      idNumberPath: 'nationalId'
    });

    const {isPep, parties} = data.pep;
    if (!isPep) return;

    if (parties.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pep', 'parties'],
        message: 'common:required'
      });
      return;
    }

    addPepPartyIssues(ctx, parties, false);
  });

export const OrganizationEntrySchema = z
  .object({
    companyName: z.string().min(1, 'common:required'),
    incorporationNumber: z.string().min(1, 'common:required'),
    incorporationDate: z.string().min(1, 'common:required'),
    address: z.string().min(1, 'common:required'),
    companyDocumentIds: z.array(z.string()).default([]),
    taxResidency: z
      .union([z.nativeEnum(TaxResidency), z.literal('')])
      .refine(v => v !== '', {message: 'common:required'}),
    taxResidencyCountry: z.string().default(''),
    pep: buyerPepSchema
  })
  .superRefine((data, ctx) => {
    if (data.taxResidency === TaxResidency.OTHER && !data.taxResidencyCountry.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['taxResidencyCountry'],
        message: 'common:required'
      });
    }

    addFutureDateIssue(ctx, 'incorporationDate', data.incorporationDate);

    addIndividualIdentityIssues(ctx, {
      isCorporate: true,
      isPassport: false,
      idNumber: data.incorporationNumber,
      idNumberPath: 'incorporationNumber'
    });

    const {isPep, parties} = data.pep;
    if (!isPep) return;

    if (parties.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pep', 'parties'],
        message: 'common:required'
      });
      return;
    }

    addPepPartyIssues(ctx, parties, true);
  });

export const SignatoryEntrySchema = z
  .object({
    fullName: z.string().min(1, 'common:required'),
    nationalId: z.string().min(1, 'common:required'),
    phone: z.string().min(1, 'common:required'),
    email: z.string().min(1, 'common:required')
  })
  .superRefine((data, ctx) => {
    addFullNameIssue(ctx, data.fullName);
    addMobilePhoneIssue(ctx, data.phone);
    addEmailIssue(ctx, data.email);
    addIndividualIdentityIssues(ctx, {
      isPassport: false,
      idNumber: data.nationalId,
      idNumberPath: 'nationalId'
    });
  });

/** One row of a mixed buyer group. `person`/`organization`/`signatories` are all always present — RHF needs stable field paths — but only the `partyType`-active branch is submitted. */
export const BuyerEntryFormSchema = z.object({
  partyType: z.nativeEnum(PartyType),
  person: BuyerEntrySchema,
  organization: OrganizationEntrySchema,
  signatories: z.array(SignatoryEntrySchema).min(1)
});

export const CreateBuyerFormSchema = z.object({
  buyers: z.array(BuyerEntryFormSchema).min(1, 'common:required'),
  asset: AssetSchema,
  purchasePriceNis: z.string().min(1, 'common:required')
});

export type BuyerEntryFormValues = z.infer<typeof BuyerEntryFormSchema>;
export type CreateBuyerFormValues = z.infer<typeof CreateBuyerFormSchema>;

export const EMPTY_BUYER_ENTRY: z.infer<typeof BuyerEntrySchema> = {
  buyerKey: '',
  fullName: '',
  nationalId: '',
  idType: IdentityDocumentType.NATIONAL_ID,
  idCountry: '',
  identityDocumentId: '',
  dateOfBirth: '',
  phone: '',
  email: '',
  address: '',
  taxResidency: '' as TaxResidency,
  taxResidencyCountry: '',
  pep: {isPep: false, parties: []}
};

export const EMPTY_SIGNATORY_ENTRY: z.infer<typeof SignatoryEntrySchema> = {
  fullName: '',
  nationalId: '',
  phone: '',
  email: ''
};

export const EMPTY_ORGANIZATION_ENTRY: z.infer<typeof OrganizationEntrySchema> = {
  companyName: '',
  incorporationNumber: '',
  incorporationDate: '',
  address: '',
  companyDocumentIds: [],
  taxResidency: '' as TaxResidency,
  taxResidencyCountry: '',
  pep: {isPep: false, parties: []}
};

const EMPTY_BUYER_FORM_ENTRY: BuyerEntryFormValues = {
  partyType: PartyType.PERSON,
  person: EMPTY_BUYER_ENTRY,
  organization: EMPTY_ORGANIZATION_ENTRY,
  signatories: [EMPTY_SIGNATORY_ENTRY]
};

/** Each row needs its own `buyerKey` — it scopes that buyer's identity upload before a partyId exists. */
export function createEmptyBuyerFormEntry(
  partyType: PartyType = PartyType.PERSON
): BuyerEntryFormValues {
  const entry = structuredClone(EMPTY_BUYER_FORM_ENTRY);
  entry.partyType = partyType;
  entry.person.buyerKey = crypto.randomUUID();
  return entry;
}

type AssetFormValues = CreateBuyerFormValues['asset'];

/** The one source of truth for "every asset field, blank" — a new AssetFieldKey needs no update here. */
export const BLANK_ASSET_FIELDS: Record<AssetFieldKey, string> = Object.fromEntries(
  ASSET_FIELD_KEYS.map(key => [key, ''])
) as Record<AssetFieldKey, string>;

function buildEmptyAsset(assetType: AssetType | ''): AssetFormValues {
  return {assetType, ...BLANK_ASSET_FIELDS, attachments: []};
}

/** Pins `assetType` when the trust type allows only one — a general-sale form leaves it unset. */
export function buildEmptyCreateBuyerFormValues(
  allowedAssetTypes: AssetType[]
): CreateBuyerFormValues {
  const [only] = allowedAssetTypes;
  return {
    buyers: [],
    asset: buildEmptyAsset(allowedAssetTypes.length === 1 && only ? only : ''),
    purchasePriceNis: ''
  };
}

/** Drops blanks and fields the asset type doesn't accept, so the form and the payload can't drift. */
export function pickAssetAttributes(
  assetType: AssetType,
  values: Record<AssetFieldKey, string>
): Partial<Record<AssetFieldKey, string>> {
  const picked: Partial<Record<AssetFieldKey, string>> = {};
  for (const key of getAssetFieldKeys(assetType)) {
    const trimmed = values[key].trim();
    if (trimmed) picked[key] = trimmed;
  }
  return picked;
}
